import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { InstantSearchSSRProvider } from 'react-instantsearch-hooks-web';
// import { getServerState } from '@/lib/algolia/getServerState';
import { getServerState } from 'react-instantsearch-hooks-server';
import Layout from '@/components/layout';
import { Search } from '@/cataloguePages/productCategory/Search';
import { configuredSanityClient } from '@/lib/sanity/sanityClient';
import { getNavigation } from '@/lib/sanity/queries';
import { CategoryHero } from '@fagmobler/ui';
import DefaultImage from '@/components/images/DefaultImage';
import Debug from '@/components/debug';
import groq from 'groq';
import { usePreviewSubscribtion } from '@/lib/sanity/hooks';
import type { MaterializedSeriePage } from '@fagmobler/sanity';
import { serieProjection } from '@fagmobler/sanity';
import { FagmoblerPortableText } from '@/components/portableText';
import { NextSeo } from 'next-seo';
import { getSerieNameFromSlug } from '@/lib/algolia/utils';
import { renderToString } from 'react-dom/server';
import { baseUrl } from '@/lib/utils/getUrls';
import { SearchProps } from '@/lib/algolia/createCategorySearchProps';
import { SerieSearch } from '@/cataloguePages/serie/SerieSearch';
import { QueryClient, QueryClientProvider } from 'react-query';
import { siteUrl } from '@/next-sitemap.config';

export interface Params extends ParsedUrlQuery {
  slugs?: string | string[];
}

export const getPathsSerie = async () => {
  return configuredSanityClient
    .fetch<{ slug: { current?: string } | null; _updatedAt: string }[]>(
      `*[_type == "serie" && defined(slug.current) && !(_id in path("drafts.**"))] {
          slug,
          _updatedAt
      }`
    )
    .then((docs) => {
      return docs.map((doc) => {
        if (doc.slug?.current) {
          return {
            path: `/serie/${doc.slug.current}`,
            slug: doc.slug.current,
            _updatedAt: doc._updatedAt,
          };
        }
      });
    });
};

const searchPlaceholder = 'Søk i serien…';

const seriePageQuery = groq`*[
    _type == "serie" &&
    slug.current == $slug
  ][0] ${serieProjection}
`;

const seriePagePreviewQuery = groq`*[
  _type == "serie" &&
  _id == $id
][0] ${serieProjection}
`;

type MaterializedSerieWithImage = MaterializedSeriePage & {
  mainImageUrl?: string | null;
};

const populateWithImageUrl = async (
  serie: MaterializedSeriePage | undefined
) => {
  // const { getSanityImageUrl } = await import('@/lib/sanity/sanityImage');
  // const mainImageUrl = serie?.mainImage
  //   ? await getSanityImageUrl(serie.mainImage)
  //   : null;
  return {
    ...serie,
    // mainImageUrl,
  } as MaterializedSerieWithImage;
};

const SeriePage = ({
  searchProps,
  serie: initialSerie,
  preview,
  serieNameFromSlug,
  previewData,
  slug,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const serie = usePreviewSubscribtion<MaterializedSerieWithImage>({
    query: seriePagePreviewQuery,
    queryParams: { slug, id: previewData?.id },
    preview,
    previewData,
    initialData: initialSerie,
    transformData: populateWithImageUrl,
  });

  const mainImageUrl = serie?.mainImageUrl;

  const title =
    serie?.title || serie?.algoliaSerie || serieNameFromSlug || slug;

  const canonical = `${siteUrl}/serie/${slug}`;

  return (
    <Layout isPreview={preview}>
      <NextSeo title={title} canonical={canonical} />
      <div className="py-4 md:py-8">
        <CategoryHero
          image={
            mainImageUrl && (
              <DefaultImage
                className="w-full h-full"
                src={mainImageUrl}
                alt={`Illustrasjon av serien ${serie.title}`}
                fill
              />
            )
          }
          title={title}
          // subTitle={
          //   serie?.description && <PortableText value={serie.description} />
          // }
        />
      </div>
      <InstantSearchSSRProvider {...searchProps.searchServerState}>
        <SerieSearch {...searchProps} />
      </InstantSearchSSRProvider>

      <div className="max-w-3xl mx-auto">
        {serie?.body && <FagmoblerPortableText value={serie.body} />}
      </div>

      <Debug data={{ preview }} label="Preview" />
      <Debug data={serie} label="Serie" />
      <Debug data={{ canonical }} label="Canonical" />
    </Layout>
  );
};

export default SeriePage;

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      // {
      //   params: {
      //     slugs: ['produkter'],
      //   },
      // },
    ],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const navigation = await getNavigation();
  const { preview = false, previewData = null, params } = context;
  const slugs = params?.slug;
  const slug = typeof slugs === 'object' ? slugs[0] : slugs;

  if (!slug) {
    return {
      notFound: true,
    };
  }

  const sanitySerie = await configuredSanityClient
    .fetch<MaterializedSeriePage | null>(seriePageQuery, { slug })
    .then(async (data) => {
      if (data) return await populateWithImageUrl(data);
      return data;
    });

  const facetFilters = [
    `serie:${sanitySerie?.algoliaSerie || getSerieNameFromSlug(slug)}`,
  ];

  const searchProps: SearchProps = {
    facetFilters: facetFilters,
    placeholder: searchPlaceholder,
    showCategories: false,
    searchServerState: null,
    serverUrl: `${baseUrl}/serie/${slug}`,
    rootPath: 'serie',
    categoryRootPath: null,
    categorySlugsRootPath: null,
    hierarchicalMenu: null,
    categoryType: null,
    nextRouter: null,
    categories: null,
    categorySlugs: null,
    articleGroups: null,
    articleGroupSlugs: null,
    algoliaProductCategory: null,
    articleGroupsRootPath: null,
    articleGroupSlugsRootPath: null,
    sessionStorageCache: null,
  };

  const searchServerState = await getServerState(
    <QueryClientProvider client={new QueryClient()}>
      <SerieSearch {...searchProps} />
    </QueryClientProvider>,
    {
      renderToString,
    }
  );

  // We ignore serverside ISR rendering for now
  // searchProps.searchServerState = searchServerState;

  const hits =
    searchServerState.initialResults.try_fagmobler.results[0].hits || [];

  // 404 if noe serie in sanity and no products in algolia
  if (hits.length < 1 && !sanitySerie) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      serieNameFromSlug: getSerieNameFromSlug(slug),
      searchProps,
      navigation,
      serie: sanitySerie,
      slug,
      preview,
      previewData,
    },
    revalidate: 60 * 60,
  };
};
